// Sound and haptic feedback utilities for winning celebrations

export class CelebrationEffects {
  private static audioContext: AudioContext | null = null;

  // Initialize audio context
  private static getAudioContext(): AudioContext | null {
    if (!this.audioContext) {
      try {
        this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
      } catch (e) {
        console.warn('Audio context not supported');
        return null;
      }
    }
    return this.audioContext;
  }

  // Play success sound using Web Audio API
  static playSuccessSound(): void {
    const audioContext = this.getAudioContext();
    if (!audioContext) return;

    try {
      // Create a celebratory sound sequence
      const now = audioContext.currentTime;

      // First note (higher pitch)
      const oscillator1 = audioContext.createOscillator();
      const gainNode1 = audioContext.createGain();

      oscillator1.connect(gainNode1);
      gainNode1.connect(audioContext.destination);

      oscillator1.frequency.setValueAtTime(523.25, now); // C5
      oscillator1.frequency.setValueAtTime(659.25, now + 0.1); // E5
      oscillator1.frequency.setValueAtTime(783.99, now + 0.2); // G5

      gainNode1.gain.setValueAtTime(0, now);
      gainNode1.gain.linearRampToValueAtTime(0.3, now + 0.05);
      gainNode1.gain.exponentialRampToValueAtTime(0.01, now + 0.4);

      oscillator1.start(now);
      oscillator1.stop(now + 0.4);

      // Second note sequence (harmony)
      setTimeout(() => {
        const oscillator2 = audioContext.createOscillator();
        const gainNode2 = audioContext.createGain();

        oscillator2.connect(gainNode2);
        gainNode2.connect(audioContext.destination);

        const now2 = audioContext.currentTime;
        oscillator2.frequency.setValueAtTime(1046.5, now2); // C6

        gainNode2.gain.setValueAtTime(0, now2);
        gainNode2.gain.linearRampToValueAtTime(0.2, now2 + 0.05);
        gainNode2.gain.exponentialRampToValueAtTime(0.01, now2 + 0.3);

        oscillator2.start(now2);
        oscillator2.stop(now2 + 0.3);
      }, 200);

    } catch (e) {
      console.warn('Error playing success sound:', e);
    }
  }

  // Play different sound for multiple wins
  static playJackpotSound(): void {
    const audioContext = this.getAudioContext();
    if (!audioContext) return;

    try {
      // More elaborate sound for multiple wins
      const frequencies = [523.25, 659.25, 783.99, 1046.5]; // C5, E5, G5, C6

      frequencies.forEach((freq, index) => {
        setTimeout(() => {
          const oscillator = audioContext.createOscillator();
          const gainNode = audioContext.createGain();

          oscillator.connect(gainNode);
          gainNode.connect(audioContext.destination);

          const now = audioContext.currentTime;
          oscillator.frequency.setValueAtTime(freq, now);

          gainNode.gain.setValueAtTime(0, now);
          gainNode.gain.linearRampToValueAtTime(0.25, now + 0.05);
          gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.25);

          oscillator.start(now);
          oscillator.stop(now + 0.25);
        }, index * 100);
      });
    } catch (e) {
      console.warn('Error playing jackpot sound:', e);
    }
  }

  // Haptic feedback for mobile devices
  static triggerHapticFeedback(pattern: 'success' | 'jackpot' = 'success'): void {
    if (!navigator.vibrate) return;

    try {
      if (pattern === 'success') {
        // Short vibration for single win
        navigator.vibrate([100, 50, 100]);
      } else {
        // Longer pattern for multiple wins
        navigator.vibrate([100, 50, 100, 50, 200, 100, 300]);
      }
    } catch (e) {
      console.warn('Haptic feedback not supported:', e);
    }
  }

  // Combined celebration effect
  static celebrate(hasMultipleWins: boolean = false): void {
    if (hasMultipleWins) {
      this.playJackpotSound();
      this.triggerHapticFeedback('jackpot');
    } else {
      this.playSuccessSound();
      this.triggerHapticFeedback('success');
    }
  }

  // Play subtle interaction sounds
  static playClickSound(): void {
    const audioContext = this.getAudioContext();
    if (!audioContext) return;

    try {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      const now = audioContext.currentTime;
      oscillator.frequency.setValueAtTime(800, now);

      gainNode.gain.setValueAtTime(0, now);
      gainNode.gain.linearRampToValueAtTime(0.1, now + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.01, now + 0.1);

      oscillator.start(now);
      oscillator.stop(now + 0.1);
    } catch (e) {
      console.warn('Error playing click sound:', e);
    }
  }

  // Screen flash effect for wins
  static flashScreen(color: string = 'gold'): void {
    const flash = document.createElement('div');
    flash.style.position = 'fixed';
    flash.style.top = '0';
    flash.style.left = '0';
    flash.style.width = '100vw';
    flash.style.height = '100vh';
    flash.style.backgroundColor = color;
    flash.style.opacity = '0.3';
    flash.style.pointerEvents = 'none';
    flash.style.zIndex = '9999';
    flash.style.transition = 'opacity 0.3s ease-out';

    document.body.appendChild(flash);

    // Trigger fade out
    setTimeout(() => {
      flash.style.opacity = '0';
    }, 50);

    // Remove element
    setTimeout(() => {
      document.body.removeChild(flash);
    }, 350);
  }
}